// src/lib/exportExpenses.ts

import * as Sentry from '@sentry/react';
import type { Gasto } from '@/components/hooks/useExpenses';

// ==========================================
// TIPOS
// ==========================================

export type ExpenseColumn = {
  label: string;
  enabled: boolean;
  exportValue: (gasto: Gasto) => string | number | null | undefined;
};

// ==========================================
// UTILIDAD
// ==========================================

const normalizeValue = (value: string | number | null | undefined) =>
  value == null ? '' : value;

// ==========================================
// EXPORT EXCEL DINÁMICO (GASTOS) - EXCELJS
// ==========================================

export async function exportExpensesToExcelDynamic(
  gastos: Gasto[],
  columns: ExpenseColumn[],
  fileName = 'gastos.xlsx'
) {
  try {
    const ExcelJS = (await import('exceljs')).default;

    const activeColumns = columns.filter((c) => c.enabled);

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Bookoll';
    wb.created = new Date();

    const ws = wb.addWorksheet('Gastos', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });

    ws.columns = activeColumns.map((c) => ({
      header: c.label,
      key: c.label,
      width: Math.min(Math.max(c.label.length + 6, 12), 45),
    }));

    const header = ws.getRow(1);
    header.height = 20;

    header.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: true,
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' },
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      };
    });

    gastos.forEach((g, idx) => {
      const values = activeColumns.map((c) => normalizeValue(c.exportValue(g)));
      const row = ws.addRow(values);
      row.height = 18;

      const zebra = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
          wrapText: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: zebra },
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          left: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          bottom: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          right: { style: 'thin', color: { argb: 'FFF3F4F6' } },
        };
      });
    });

    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: activeColumns.length },
    };

    const moneyLabels = new Set(['Base', 'Total', 'Importe', 'Base imponible']);
    const percentLabels = new Set(['IVA', 'IVA %', '% IVA']);

    activeColumns.forEach((c, i) => {
      const col = ws.getColumn(i + 1);

      if (moneyLabels.has(c.label)) {
        col.numFmt = '#,##0.00" €"';
        col.alignment = { vertical: 'middle', horizontal: 'right' };
      }

      if (percentLabels.has(c.label)) {
        col.numFmt = '0.00" %"';
        col.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });

    activeColumns.forEach((c, i) => {
      const colIdx = i + 1;
      let maxLen = c.label.length;

      ws.getColumn(colIdx).eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value;
        const s = v == null ? '' : String(v);
        if (s.length > maxLen) maxLen = s.length;
      });

      ws.getColumn(colIdx).width = Math.min(Math.max(maxLen + 2, 12), 45);
    });

    const buf = await wb.xlsx.writeBuffer();

    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fileName, gastosCount: gastos.length },
    });
    throw err;
  }
}

// ==========================================
// EXPORT PDF DINÁMICO (GASTOS)
// ==========================================

export async function exportExpensesToPDFDynamic(
  gastos: Gasto[],
  columns: ExpenseColumn[],
  fileName = 'gastos.pdf'
) {
  try {
    const jsPDF = (await import('jspdf')).default;
    const autoTable = (await import('jspdf-autotable')).default;

    const doc = new jsPDF();
    const activeColumns = columns.filter((c) => c.enabled);
    const headers = activeColumns.map((c) => c.label);
    const body = gastos.map((g) =>
      activeColumns.map((c) => normalizeValue(c.exportValue(g)))
    );

    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [16, 50, 84],
        textColor: 255,
        fontStyle: 'bold',
      },
      margin: { top: 20 },
    });

    doc.save(fileName);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fileName, gastosCount: gastos.length },
    });
    throw err;
  }
}

// ==========================================
// EXPORT ZIP (RECIBOS DE GASTOS)
// ==========================================

export async function exportExpenseReceiptsToZip(
  gastos: Gasto[],
  fileName = 'recibos.zip'
) {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    const gastosConRecibo = (gastos || []).filter(
      (g: any) => g?.recibos?.[0]?.url
    );

    if (gastosConRecibo.length === 0) {
      return { ok: false as const, added: 0 };
    }

    const usedNames = new Map<string, number>();

    for (const g of gastosConRecibo) {
      const reciboUrl: string | undefined = (g as any)?.recibos?.[0]?.url;
      if (!reciboUrl) continue;

      const res = await fetch(reciboUrl);
      if (!res.ok) continue;

      const blob = await res.blob();

      const ext =
        blob.type === 'application/pdf'
          ? 'pdf'
          : blob.type.startsWith('image/')
          ? 'jpg'
          : 'file';

      const concepto = ((g as any).concepto || 'recibo')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
        .replace(/\.+$/g, '');

      const rawDate = (g as any).fecha_inicio;
      const d = rawDate ? new Date(rawDate) : new Date();
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      const año = d.getFullYear();

      let baseName = `${concepto}_${mes}_${año}.${ext}`;

      const prev = usedNames.get(baseName) ?? 0;
      usedNames.set(baseName, prev + 1);

      if (prev > 0) {
        baseName = baseName.replace(`.${ext}`, `_${prev + 1}.${ext}`);
      }

      zip.file(baseName, blob);
    }

    const content = await zip.generateAsync({ type: 'blob' });

    const url = window.URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { ok: true as const, added: gastosConRecibo.length };
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fileName, gastosCount: gastos.length },
    });
    throw err;
  }
}
